import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";

const ContentCopyIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 18C8.45 18 7.97917 17.8042 7.5875 17.4125C7.19583 17.0208 7 16.55 7 16V4C7 3.45 7.19583 2.97917 7.5875 2.5875C7.97917 2.19583 8.45 2 9 2H18C18.55 2 19.0208 2.19583 19.4125 2.5875C19.8042 2.97917 20 3.45 20 4V16C20 16.55 19.8042 17.0208 19.4125 17.4125C19.0208 17.8042 18.55 18 18 18H9ZM9 16H18V4H9V16ZM5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6H5V20H16V22H5Z"
      fill="#039EC3"
    />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#ECF9F3",
    borderRadius: theme.spacing(0.5),
    boxShadow: "0 4px 8px 0 rgba(0, 69, 85, 0.16), 0 4px 12px 4px rgba(0, 69, 85, 0.08)",
    width: theme.spacing(92),
    maxWidth: theme.spacing(80),
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  checkIcon: {
    color: "#43A047",
    display: "flex",
    width: theme.spacing(3),
    height: theme.spacing(3),
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    aspectRatio: "1 / 1",
  },
  title: {
    fontWeight: 600,
    fontSize: theme.spacing(2),
    fontStyle: "normal",
    fontFamily: "Figtree",
    lineHeight: 1.2,
    color: "#03804B",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  copyButton: {
    color: "#039EC3",
    textTransform: "none",
    textAlign: "center",
    fontWeight: 600,
    fontSize: theme.spacing(2),
    fontStyle: "normal",
    fontFamily: "Figtree",
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 1.5),
  },
  buttonIcon: {
    fontSize: theme.spacing(2),
    marginRight: theme.spacing(0.75),
  },
}));

interface Props {
  url: string;
  onDismiss: () => void;
}

const RecordingShareToast = ({ url, onDismiss }: Props) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopy = () => {
    let safeUrl: string;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:") {
        setCopyFailed(true);
        return;
      }
      safeUrl = parsed.href;
    } catch {
      setCopyFailed(true);
      return;
    }

    navigator.clipboard
      .writeText(safeUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          onDismiss();
        }, 1500);
      })
      .catch(() => {
        setCopyFailed(true);
      });
  };

  return (
    <div className={classes.root} role="alert">
      <div className={classes.left}>
        <CheckCircleIcon className={classes.checkIcon} />
        <span className={classes.title}>Recording complete</span>
      </div>
      <div className={classes.actions}>
        <Button className={classes.copyButton} onClick={handleCopy} disableElevation>
          <ContentCopyIcon className={classes.buttonIcon} />
          {copied ? "Copied!" : copyFailed ? "Copy failed" : "Copy recording link"}
        </Button>
      </div>
    </div>
  );
};

export default RecordingShareToast;
